import { create } from 'zustand';
import { fetchMijnWerk, postMelding } from '@/api/mijnWerk';
import type { MijnWerkPoRef, MijnWerkResponse } from '@/api/mijnWerk';

interface MijnWerkState {
  /** null = let the API pick: the open PO, else the newest one. */
  selectedPoId: number | null;
  data: MijnWerkResponse | null;
  /**
   * The purchase orders this OA may open. Held apart from `data` because
   * switching PO clears `data`: without this the desktop rail's PO list would
   * unmount on every click and come back scrolled to the top.
   */
  pos: MijnWerkPoRef[];
  loading: boolean;
  error: string | null;

  /** Switch to another PO and clear cached data. Call fetchData() afterwards. */
  selectPo: (poId: number) => void;
  fetchData: () => Promise<void>;
  /**
   * Flag one PO line. The new flag is merged into the loaded screen rather than
   * refetched: the OA is looking at a long list and must not lose their place.
   * Throws on failure so the sheet can keep the text the OA typed.
   */
  submitMelding: (poLineId: number, text: string) => Promise<void>;
}

/**
 * Every fetch takes a ticket and only the newest one may write. Switching purchase
 * order twice in a row could otherwise leave the FIRST reply landing last: the
 * statement on screen would then be a different purchase order from the one
 * highlighted beside it, and the same-PO guard would refuse to correct it. A late
 * failure must not overwrite a newer success either.
 */
let requestTicket = 0;

export const useMijnWerkStore = create<MijnWerkState>((set, get) => ({
  selectedPoId: null,
  data: null,
  pos: [],
  loading: false,
  error: null,

  selectPo: (poId) => {
    set({ selectedPoId: poId, data: null, error: null });
  },

  fetchData: async () => {
    const { selectedPoId } = get();
    const ticket = ++requestTicket;
    set({ loading: true, error: null });
    try {
      const data = await fetchMijnWerk(selectedPoId ?? undefined);
      if (ticket !== requestTicket) return;
      set({ data, pos: data.pos, loading: false });
    } catch (err) {
      if (ticket !== requestTicket) return;
      const message = err instanceof Error ? err.message : 'Ophalen mislukt';
      set({ error: message, loading: false });
    }
  },

  submitMelding: async (poLineId, text) => {
    const melding = await postMelding(poLineId, text);
    const { data } = get();
    if (!data) return;
    // One flag per line: a second comment extends the open one, so replace by
    // line rather than appending a second entry for the same row.
    const meldingen = [
      ...(data.meldingen ?? []).filter(m => m.poLineId !== melding.poLineId || m.status !== 'open'),
      melding,
    ];
    set({ data: { ...data, meldingen } });
  },
}));
